import * as pdfjs from "pdfjs-dist";
import workerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import {
  PDF_MAX_BYTES,
  PDF_PREVIEW_PIXELS,
  PDF_PREVIEW_TIMEOUT_MS,
  pdfPreviewSize,
} from "../shared/pdf-preview";

pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;
let active = false;

/** A bounded display preview, never validation of the original PDF's fidelity. */
export async function pdfPreview(
  bytes: Uint8Array,
  pageNumber = 1,
  signal?: AbortSignal,
  maxEdge?: number,
) {
  signal?.throwIfAborted();
  if (bytes.byteLength > PDF_MAX_BYTES)
    throw new Error("Please use a PDF smaller than 30 MB.");
  if (!Number.isInteger(pageNumber) || pageNumber < 1)
    throw new Error("Choose a whole PDF page number starting at 1.");
  if (active) throw new Error("Wait for the current PDF preview to finish.");
  active = true;
  let render: pdfjs.RenderTask | undefined;
  let canvas: HTMLCanvasElement | undefined;
  let task: pdfjs.PDFDocumentLoadingTask | undefined;
  let destroy: Promise<void> | undefined;
  let timedOut = false;
  const dispose = () => {
    render?.cancel();
    if (task && !destroy) {
      destroy = task.destroy();
      // Awaited in finally as well; attach a handler now during cancellation.
      void destroy.catch(() => {});
    }
  };
  const timeout = setTimeout(() => {
    timedOut = true;
    dispose();
  }, PDF_PREVIEW_TIMEOUT_MS);
  signal?.addEventListener("abort", dispose, { once: true });
  const check = () => {
    signal?.throwIfAborted();
    if (timedOut)
      throw new Error("PDF preview took too long. Try a simpler page.");
  };
  try {
    task = pdfjs.getDocument({
      data: bytes.slice(),
      stopAtErrors: true,
      canvasMaxAreaInBytes: PDF_PREVIEW_PIXELS * 4,
      // PDF.js may omit images above this limit; previews are explicitly not originals.
      maxImageSize: 40_000_000,
    });
    const doc = await task.promise;
    check();
    if (pageNumber > doc.numPages)
      throw new Error(`This PDF has ${doc.numPages} page(s).`);
    const page = await doc.getPage(pageNumber);
    check();
    const base = page.getViewport({ scale: 1 });
    const size = pdfPreviewSize(base.width, base.height);
    const thumbnailScale =
      maxEdge && Number.isFinite(maxEdge)
        ? Math.min(1, Math.max(32, maxEdge) / Math.max(size.width, size.height))
        : 1;
    const viewport = page.getViewport({ scale: size.scale * thumbnailScale });
    canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.ceil(viewport.width));
    canvas.height = Math.max(1, Math.ceil(viewport.height));
    render = page.render({ canvas, viewport });
    await render.promise;
    check();
    const blob = await new Promise<Blob>((resolve, reject) =>
      canvas!.toBlob(
        (value) =>
          value ? resolve(value) : reject(new Error("Cannot render this PDF.")),
        "image/png",
      ),
    );
    check();
    return {
      blob,
      w: 1120,
      h: (1120 * base.height) / base.width,
      pages: doc.numPages,
      paperWidthMm: (base.width * 25.4) / 72,
    };
  } catch (error) {
    check();
    throw error;
  } finally {
    clearTimeout(timeout);
    signal?.removeEventListener("abort", dispose);
    dispose();
    try {
      await destroy;
    } finally {
      if (canvas) {
        canvas.width = 0;
        canvas.height = 0;
      }
      active = false;
    }
  }
}
